import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, arrayRemove } from 'firebase/firestore';
import { USERS_DB } from '@/constants/constants.js';

/**
 * Resolve an array of UIDs to their display aliases.
 * Returns [{ uid, alias }] for each UID found.
 * @param {string[]} uids
 * @returns {Promise<{ uid: string, alias: string }[]>}
 */
export async function resolveUidsToAliases(uids) {
  if (!uids?.length) return [];
  try {
    const snap = await getDocs(query(
      collection(db, USERS_DB),
      where('user_id', 'in', uids.slice(0, 30))
    ));
    return snap.docs.map(d => ({ uid: d.data().user_id, alias: d.data().alias ?? '' }));
  } catch (e) {
    console.error('resolveUidsToAliases error:', e);
    return [];
  }
}

/**
 * Get all users who have allowed the given tournament runner (by UID) to add them to tournaments.
 * Returns options for the player-name dropdown: { alias, user_id } for every user who has granted
 * access, regardless of poker permission. Poker approval is only relevant when saving results
 * (poker users get a session entry; others have their name and result recorded without one).
 * @param {string} tournamentRunnerUid - user_id (UID) of the current user (tournament runner)
 * @returns {Promise<{ alias: string, user_id: string }[]>}
 */
export async function getAllowedPlayerOptions(tournamentRunnerUid) {
  const uid = (tournamentRunnerUid || '').trim();
  if (!uid) return [];
  try {
    const usersRef = collection(db, USERS_DB);
    const q = query(
      usersRef,
      where('allowedTournamentEnterers', 'array-contains', uid)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return { alias: data.alias ?? '', user_id: data.user_id };
    }).filter(o => o.alias);
  } catch (e) {
    console.error('getAllowedPlayerOptions error:', e);
    return [];
  }
}

/**
 * Get all users who have granted the given tournament runner access to add them to tournaments.
 * Returns all such users regardless of poker permission status.
 * @param {string} runnerUid - user_id (UID) of the tournament runner
 * @returns {Promise<{ alias: string, user_id: string }[]>}
 */
export async function getPeopleGrantedAccess(runnerUid) {
  const uid = (runnerUid || '').trim();
  if (!uid) return [];
  try {
    const usersRef = collection(db, USERS_DB);
    const q = query(
      usersRef,
      where('allowedTournamentEnterers', 'array-contains', uid)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
      const data = d.data();
      return { alias: data.alias ?? '', user_id: data.user_id };
    }).filter(o => o.alias);
  } catch (e) {
    console.error('getPeopleGrantedAccess error:', e);
    return [];
  }
}

/**
 * Remove the runner's UID from a target user's allowedTournamentEnterers list.
 * Used when a tournament runner wants to block/revoke a person's grant.
 * @param {string} targetUserUid - user_id (UID) of the user whose list will be modified
 * @param {string} runnerUid - UID (user_id) to remove from their allowedTournamentEnterers
 */
export async function removeRunnerFromUserEnterers(targetUserUid, runnerUid) {
  const trimmedTarget = (targetUserUid || '').trim();
  const trimmedRunner = (runnerUid || '').trim();
  if (!trimmedTarget || !trimmedRunner) return;
  const usersRef = collection(db, USERS_DB);
  const snapshot = await getDocs(query(usersRef, where('user_id', '==', trimmedTarget)));
  if (snapshot.empty) return;
  await updateDoc(doc(db, USERS_DB, snapshot.docs[0].id), {
    allowedTournamentEnterers: arrayRemove(trimmedRunner),
  });
}

/**
 * Look up a user by alias (username). Returns user_id, alias, and allowedTournamentEnterers
 * if a user exists with that alias (poker permission not required). Non–poker-tracker users
 * can still be linked for tournament results (name/result stored) but no session is created.
 * @param {string} alias - Username/alias to look up (will be trimmed)
 * @returns {Promise<{ user_id: string, alias: string, allowedTournamentEnterers: string[], pokerApproved?: boolean } | null>}
 */
export async function lookupUserByAlias(alias) {
  const trimmed = (alias || '').trim().toLowerCase();
  if (!trimmed) return null;
  try {
    const usersRef = collection(db, USERS_DB);
    // Query aliasLower for case-insensitive lookup; fall back to a case-insensitive scan of alias for legacy docs
    let snapshot = await getDocs(query(usersRef, where('aliasLower', '==', trimmed)));
    if (snapshot.empty) {
      // Legacy docs may lack aliasLower and store alias in original case,
      // so fetch candidates by exact-case alias, then filter case-insensitively.
      const candidates = await getDocs(usersRef);
      const match = candidates.docs.find(d => (d.data().alias ?? '').toLowerCase() === trimmed);
      if (!match) return null;
      const data = match.data();
      const pokerApproved = data.permissions?.poker === 'approved';
      return {
        user_id: data.user_id,
        alias: data.alias ?? trimmed,
        allowedTournamentEnterers: Array.isArray(data.allowedTournamentEnterers) ? data.allowedTournamentEnterers : [],
        pokerApproved
      };
    }
    const userDoc = snapshot.docs[0];
    const data = userDoc.data();
    const pokerApproved = data.permissions?.poker === 'approved';
    return {
      user_id: data.user_id,
      alias: data.alias ?? trimmed,
      allowedTournamentEnterers: Array.isArray(data.allowedTournamentEnterers) ? data.allowedTournamentEnterers : [],
      pokerApproved
    };
  } catch (e) {
    console.error('lookupUserByAlias error:', e);
    return null;
  }
}
